import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { setCurrencies, setRegions } from "../../../redux/actions/main_actions";
import * as api from "../../../api";

const SubCategories = ({ category, onSubmit }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Form state (replaces Ant Design Form)
  const [formValues, setFormValues] = useState({});
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedCurrencyId, setSelectedCurrencyId] = useState(null);
  const [options, setOptions] = useState([]);
  const [selectedRegionId, setSelectedRegionId] = useState(null);
  const [selectedCityId, setSelectedCityId] = useState(null);
  
  const { currencies, regions } = useSelector((state) => state.main);

  // Update a single form field
  const updateField = (name, value) => {
    setFormValues(prev => ({ ...prev, [name]: value }));
  };

  // API calls
  const fetchRegions = async () => {
    try {
      const [regionsData, currenciesData] = await Promise.all([
        api.fetchRegions(),
        api.fetchCurrencies()
      ]);

      if (regionsData) {
        dispatch(setRegions(regionsData));
        handleInitialRegion(regionsData);
      }

      if (currenciesData) {
        dispatch(setCurrencies(currenciesData));
        handleInitialCurrency(currenciesData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleInitialRegion = (regionsData) => {
    if (!regionsData.length) return;
    const region = formValues.region_id
      ? regionsData.find(r => r.id === formValues.region_id)
      : regionsData[0];
    if (region) selectRegion(region);
  };

  const handleInitialCurrency = (currenciesData) => {
    if (!currenciesData.length) return;
    const currencyId = formValues.currency_id
      ? currenciesData.find(c => c.id === formValues.currency_id)?.id
      : currenciesData[0].id;
    setSelectedCurrencyId(currencyId);
    updateField('currency_id', currencyId);
  };

  // Form handlers
  const selectRegion = (region) => {
    if (!region) return;
    setSelectedRegionId(region.id);
    updateField('region_id', region.id);
    setCities(region.cities || []);

    const city = formValues.city_id
      ? region.cities?.find(c => c.id === formValues.city_id)
      : region.cities?.[0];
    if (city) selectCity(city);
  };

  const selectCity = (city) => {
    if (!city) return;
    setSelectedCityId(city.id);
    updateField('city_id', city.id);
    setDistricts(city.districts || []);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (onSubmit) {
      // Mimic antd Form API: pass an object with getFieldsValue
      const formApi = {
        getFieldsValue: () => formValues,
        getFieldValue: (name) => formValues[name],
        setFieldsValue: (values) => setFormValues(prev => ({ ...prev, ...values })),
      };
      onSubmit(formApi);
    }
    setIsModalVisible(false);
  };

  const handleCategoryChange = (categoryId) => {
    navigate(`/category/${categoryId}`);
    fetchRegions();
    window.location.reload();
  };

  // Category options management
  const initializeCategoryOptions = (categoryData) => {
    if (!categoryData?.children) return;
    const newOptions = categoryData.children.map(item => ({
      label: item.name,
      value: item.id
    }));
    setOptions(newOptions);
  };

  useEffect(() => {
    fetchRegions();
    initializeCategoryOptions(category);
  }, [category]);

  if (!category?.children?.length) {
    return null;
  }

  // Custom attribute field renderer (pure Tailwind CSS)
  const renderCustomField = (item) => {
    let values = {};
    try {
      values = typeof item.values === 'string' ? JSON.parse(item.values) : item.values || {};
    } catch (e) {
      values = {};
    }

    switch (item.type) {
      case 'TEXT':
        return (
          <textarea
            id={item.name}
            rows={3}
            placeholder={item.title}
            value={formValues[item.name] || ''}
            onChange={(e) => updateField(item.name, e.target.value)}
            className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all duration-200 resize-none"
          />
        );
      case 'INT':
        return (
          <input
            type="number"
            id={item.name}
            placeholder={item.placeholder || item.title}
            value={formValues[item.name] || ''}
            onChange={(e) => updateField(item.name, e.target.value)}
            className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all duration-200"
          />
        );
      case 'SELECT':
      case 'RELATION':
        return (
          <select
            id={item.name}
            value={formValues[item.name] || ''}
            onChange={(e) => updateField(item.name, e.target.value)}
            className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all duration-200 appearance-none cursor-pointer"
          >
            <option value="">{item.title}</option>
            {values && Object.keys(values).map((key) => (
              <option key={key} value={values[key]}>{values[key]}</option>
            ))}
          </select>
        );
      case 'BOOLEAN':
        return (
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={!!formValues[item.name]}
              onChange={(e) => updateField(item.name, e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
          </label>
        );
      case 'STRING':
      default:
        return (
          <input
            type="text"
            id={item.name}
            placeholder={item.placeholder || item.title}
            value={formValues[item.name] || ''}
            onChange={(e) => updateField(item.name, e.target.value)}
            className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all duration-200"
          />
        );
    }
  };

  const renderSearchForm = () => (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Custom Attributes */}
      {category?.custom_attribute?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {category.custom_attribute.map((item) => (
            <div key={item.name} className="space-y-1.5">
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">
                {item.title}
                {item.is_required && <span className="text-rose-500 ml-0.5">*</span>}
              </label>
              {renderCustomField(item)}
            </div>
          ))}
        </div>
      )}

      {/* Price Range and Currency */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">Цена от</label>
          <input
            type="number"
            placeholder="0"
            value={formValues.price_from || ''}
            onChange={(e) => updateField('price_from', e.target.value)}
            className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all duration-200"
          />
        </div>
        <div className="space-y-1.5">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">Цена до</label>
          <input
            type="number"
            placeholder="999 999"
            value={formValues.price_to || ''}
            onChange={(e) => updateField('price_to', e.target.value)}
            className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all duration-200"
          />
        </div>
        <div className="space-y-1.5">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">Валюта</label>
          <select
            value={selectedCurrencyId || ''}
            onChange={(e) => {
              const val = parseInt(e.target.value);
              setSelectedCurrencyId(val);
              updateField('currency_id', val);
            }}
            className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all duration-200 appearance-none cursor-pointer"
          >
            {currencies.map((item) => (
              <option key={item.id} value={item.id}>{item.symbol}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Region and City Selection */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">Регион</label>
          <select
            value={selectedRegionId || ''}
            onChange={(e) => {
              const id = parseInt(e.target.value);
              const region = regions.find(r => r.id === id);
              selectRegion(region);
            }}
            className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all duration-200 appearance-none cursor-pointer"
          >
            <option value="">Выберите регион</option>
            {regions.map((item) => (
              <option key={item.id} value={item.id}>{item.name}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">Город</label>
          <select
            value={selectedCityId || ''}
            onChange={(e) => {
              const id = parseInt(e.target.value);
              const city = cities.find(c => c.id === id);
              selectCity(city);
            }}
            className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all duration-200 appearance-none cursor-pointer"
          >
            <option value="">Выберите город</option>
            {cities.map((item) => (
              <option key={item.id} value={item.id}>{item.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-2">
        <button
          type="submit"
          className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-semibold text-sm rounded-xl shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 hover:from-blue-700 hover:to-blue-600 active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          Поиск
        </button>
      </div>
    </form>
  );

  return (
    <>
      {/* Desktop Search Form */}
      <div className="hidden xl:block px-4 pb-4">
        <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-800">Фильтры поиска</h3>
              <p className="text-xs text-slate-400">Настройте параметры для точного результата</p>
            </div>
          </div>
          {renderSearchForm()}
        </div>
      </div>

      {/* Mobile Search Button and Modal */}
      <div className="block xl:hidden my-4 px-3">
        <button
          onClick={() => setIsModalVisible(true)}
          className="w-full py-3.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-semibold text-sm rounded-xl shadow-lg shadow-blue-500/25 hover:shadow-xl hover:from-blue-700 hover:to-blue-600 active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          Поиск по фильтрам
        </button>

        {/* Custom Modal Overlay */}
        {isModalVisible && (
          <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-[fadeIn_0.2s_ease]"
              onClick={() => setIsModalVisible(false)}
            />
            {/* Modal Content */}
            <div className="relative w-full sm:max-w-xl mx-auto bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[85vh] overflow-y-auto animate-[slideUp_0.3s_ease]">
              {/* Modal Header */}
              <div className="sticky top-0 bg-white/95 backdrop-blur-md px-6 py-4 border-b border-slate-100 flex items-center justify-between rounded-t-2xl z-10">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-slate-800">Поиск по фильтрам</h3>
                </div>
                <button
                  onClick={() => setIsModalVisible(false)}
                  className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                >
                  <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {/* Modal Body */}
              <div className="p-6">
                {renderSearchForm()}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Desktop Category Links */}
      <div className="hidden md:block px-4 py-3">
        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
          <h4 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
            Подкатегории
          </h4>
          <div className="flex flex-wrap gap-2">
            {category.children.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                className="px-4 py-2 bg-slate-50 hover:bg-blue-50 border border-slate-200 hover:border-blue-300 text-slate-600 hover:text-blue-600 text-sm font-medium rounded-xl transition-all duration-200 cursor-pointer"
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile Category Dropdown */}
      <div className="block md:hidden px-3 py-2">
        <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">По категориям</label>
          {options.length > 0 && (
            <select
              defaultValue=""
              onChange={(e) => handleCategoryChange(parseInt(e.target.value))}
              className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all duration-200 appearance-none cursor-pointer"
            >
              <option value="" disabled>{category.name}</option>
              {options.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          )}
        </div>
      </div>
    </>
  );
};

export default SubCategories;